import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { WeightChangeCategoryControllerService, WeightReportControllerService } from '@app/generated/service';
import {
  WeightChangeCategoryDto,
  WeightLineRequest,
  WeightReportLineDto,
  WeightReportSchemaDto,
} from '@app/generated/model';
import { WeightReportAttachmentDto } from '@app/generated/model/weightReportAttachmentDto';
import { ChangeLogService } from '@app/core/services/change-log.service';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { WO_CREATE_WEIGHT_LINE, WO_DELETE_WEIGHT_LINE, WO_UPDATE_WEIGHT_LINE } from '@app/actions';

const OFFLINE_TYPE = 'WEIGHT_REPORTS';

const LINE_CHANGE_TYPES: string[] = [WO_CREATE_WEIGHT_LINE, WO_UPDATE_WEIGHT_LINE, WO_DELETE_WEIGHT_LINE];

export interface WeightLineCounts {
  sent: number;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class WeightReportService {
  private readonly countsSubject = new BehaviorSubject<WeightLineCounts>({ sent: 0, total: 0 });
  readonly counts$ = this.countsSubject.asObservable();

  constructor(
    private weightReportController: WeightReportControllerService,
    private categoryController: WeightChangeCategoryControllerService,
    private changeLogService: ChangeLogService,
    private offlineData: OfflineDataDb,
  ) {}

  getCategories(projectId: string): Observable<WeightChangeCategoryDto[]> {
    return this.categoryController.getWeightChangeCategories(projectId);
  }

  /** Schemas with not-yet-synced offline line edits overlaid on the open schema. */
  async getSchemas(workOrderId: string): Promise<WeightReportSchemaDto[]> {
    let schemas: WeightReportSchemaDto[] = [];

    if (navigator.onLine) {
      try {
        schemas = (await firstValueFrom(this.weightReportController.getWeightReports(workOrderId))) ?? [];
        await this.cacheSchemas(workOrderId, schemas);
      } catch {
        schemas = await this.readCachedSchemas(workOrderId);
      }
    } else {
      schemas = await this.readCachedSchemas(workOrderId);
    }

    const pending = await this.getPendingLineEvents(workOrderId);
    const merged = this.applyPendingLineEvents(schemas, pending);

    this.countsSubject.next(this.countLines(merged));
    return merged;
  }

  /** The editable schema, created server-side when online; offline falls back to the cached open schema. */
  async getOpenSchema(workOrderId: string): Promise<WeightReportSchemaDto | null> {
    if (navigator.onLine) {
      try {
        return (await firstValueFrom(this.weightReportController.getOrCreateOpenSchema(workOrderId))) ?? null;
      } catch {
        // fall through to the cached copy
      }
    }

    const cached = await this.readCachedSchemas(workOrderId);
    return cached.find((schema) => schema.status === WeightReportSchemaDto.StatusEnum.Open) ?? null;
  }

  /** Returns the created line (with server id) when online, so callers can immediately attach files; null when queued offline. */
  async addLine(
    workOrderId: string,
    schemaId: string,
    request: WeightLineRequest,
  ): Promise<WeightReportLineDto | null> {
    const payload: WeightLineRequest = { ...request, clientLineId: request.clientLineId || uuid() };

    if (navigator.onLine && schemaId) {
      return (await firstValueFrom(this.weightReportController.addLine(workOrderId, schemaId, payload))) ?? null;
    }

    await this.changeLogService.publish(WO_CREATE_WEIGHT_LINE, { workOrderId, schemaId, ...payload }, [workOrderId]);
    return null;
  }

  async updateLine(
    workOrderId: string,
    schemaId: string,
    line: WeightReportLineDto,
    request: WeightLineRequest,
  ): Promise<void> {
    const payload: WeightLineRequest = {
      ...request,
      clientLineId: line.clientLineId || request.clientLineId || uuid(),
    };

    if (navigator.onLine && schemaId && line.id) {
      await firstValueFrom(this.weightReportController.updateLine(workOrderId, schemaId, line.id, payload));
      return;
    }

    await this.changeLogService.publish(WO_UPDATE_WEIGHT_LINE, { workOrderId, schemaId, lineId: line.id, ...payload }, [
      workOrderId,
    ]);
  }

  async deleteLine(workOrderId: string, schemaId: string, line: WeightReportLineDto): Promise<void> {
    if (navigator.onLine && schemaId && line.id) {
      await firstValueFrom(this.weightReportController.deleteLine(workOrderId, schemaId, line.id));
      return;
    }

    await this.changeLogService.publish(
      WO_DELETE_WEIGHT_LINE,
      { workOrderId, schemaId, lineId: line.id, clientLineId: line.clientLineId },
      [workOrderId],
    );
  }

  async sendSchema(workOrderId: string, schemaId: string): Promise<void> {
    await firstValueFrom(this.weightReportController.sendSchema(workOrderId, schemaId));
  }

  /** Sending must not run while line edits are still queued, or the email would omit them. */
  async hasPendingLineChanges(workOrderId: string): Promise<boolean> {
    const pending = await this.getPendingLineEvents(workOrderId);
    return pending.length > 0;
  }

  countLines(schemas: WeightReportSchemaDto[]): WeightLineCounts {
    return (schemas ?? []).reduce(
      (counts, schema) => {
        const lineCount = schema.lines?.length ?? 0;
        return {
          sent: counts.sent + (schema.status === WeightReportSchemaDto.StatusEnum.Sent ? lineCount : 0),
          total: counts.total + lineCount,
        };
      },
      { sent: 0, total: 0 },
    );
  }

  listLineAttachments(workOrderId: string, schemaId: string, lineId: string): Observable<WeightReportAttachmentDto[]> {
    return this.weightReportController.getLineAttachments(workOrderId, schemaId, lineId);
  }

  uploadLineAttachment(
    workOrderId: string,
    schemaId: string,
    lineId: string,
    file: Blob,
  ): Observable<WeightReportAttachmentDto> {
    return this.weightReportController.uploadLineAttachment(workOrderId, schemaId, lineId, file);
  }

  downloadLineAttachment(
    workOrderId: string,
    schemaId: string,
    lineId: string,
    attachmentId: string,
  ): Observable<HttpResponse<string>> {
    return this.weightReportController.downloadLineAttachment(workOrderId, schemaId, lineId, attachmentId, 'response');
  }

  async download(workOrderId: string): Promise<void> {
    try {
      const schemas = (await firstValueFrom(this.weightReportController.getWeightReports(workOrderId))) ?? [];
      await this.offlineData.workOrderDataItems.put({
        workOrderId,
        type: OFFLINE_TYPE,
        data: schemas,
      });
    } catch (error) {
      console.warn(`[WEIGHT_REPORTS] Failed to download weight reports for ${workOrderId}.`, error);
    }
  }

  loadCounts(workOrderId: string): void {
    void this.getSchemas(workOrderId).catch(() => this.countsSubject.next({ sent: 0, total: 0 }));
  }

  private async cacheSchemas(workOrderId: string, schemas: WeightReportSchemaDto[]): Promise<void> {
    const isDownloaded = await this.offlineData.workOrders.where({ workOrderId }).count();
    if (!isDownloaded) {
      return;
    }

    await this.offlineData.workOrderDataItems.put({ workOrderId, type: OFFLINE_TYPE, data: schemas });
  }

  private async readCachedSchemas(workOrderId: string): Promise<WeightReportSchemaDto[]> {
    const item = await this.offlineData.workOrderDataItems.where({ workOrderId, type: OFFLINE_TYPE }).first();
    return Array.isArray(item?.data) ? item.data : [];
  }

  private async getPendingLineEvents(workOrderId: string): Promise<ChangeLogEvent[]> {
    const events = await this.changeLogService.getPending(workOrderId);
    return events.filter((event) => LINE_CHANGE_TYPES.includes(event.type));
  }

  private applyPendingLineEvents(schemas: WeightReportSchemaDto[], events: ChangeLogEvent[]): WeightReportSchemaDto[] {
    if (!events.length) {
      return schemas;
    }

    const result = schemas.map((schema) => ({ ...schema, lines: [...(schema.lines ?? [])] }));
    let openSchema = result.find((schema) => schema.status === WeightReportSchemaDto.StatusEnum.Open);

    for (const event of events) {
      const payload: any = event.content;

      if (!openSchema) {
        openSchema = { id: payload?.schemaId, status: WeightReportSchemaDto.StatusEnum.Open, lines: [] };
        result.unshift(openSchema);
      }

      const lines = openSchema.lines;
      const index = lines.findIndex(
        (line) =>
          (payload?.clientLineId && line.clientLineId === payload.clientLineId) ||
          (payload?.lineId && line.id === payload.lineId),
      );

      if (event.type === WO_DELETE_WEIGHT_LINE) {
        if (index >= 0) {
          lines.splice(index, 1);
        }
        continue;
      }

      const line = this.toLine(payload, index >= 0 ? lines[index] : null);
      if (index >= 0) {
        lines[index] = line;
      } else {
        lines.push(line);
      }
    }

    return result;
  }

  private toLine(payload: any, existing: WeightReportLineDto | null): WeightReportLineDto {
    return {
      ...(existing ?? {}),
      id: existing?.id ?? payload?.lineId,
      clientLineId: payload?.clientLineId ?? existing?.clientLineId,
      performedBy: payload?.performedBy,
      mainCategory: payload?.mainCategory,
      areaTagSystem: payload?.areaTagSystem,
      quantityMetres: payload?.quantityMetres,
      hoursSpent: payload?.hoursSpent,
      installedWeight: payload?.installedWeight,
      removedWeight: payload?.removedWeight,
      totalWeight: (payload?.installedWeight ?? 0) + (payload?.removedWeight ?? 0),
      changeFollowUpRequired: payload?.changeFollowUpRequired,
      description: payload?.description,
      comment: payload?.comment,
    };
  }
}
